import { BufferAttribute, BufferGeometry, Float32BufferAttribute, Vector3 } from 'three';

/**
 * Deletes the interproximal geometry SEGMENTATION generated and rebuilds it
 * between the crowns as CORRECTION has posed them.
 *
 * Why it has to be rebuilt at all: segmentation closes each crown's cut sides
 * with a wall, and bridges the gum across the papilla, for a scan in which the
 * teeth stand where the patient's teeth stood. Correction then moves them —
 * eight cycles of it — so by export time that geometry describes a
 * configuration that no longer exists: walls of neighbouring crowns cross,
 * stab into the gum, or leave the contact hanging in mid-air. It was never
 * meant to survive the move; it exists so the orthodontist sees a closed model
 * in the segmentation viewer, and it carries a marker (the index of its first
 * face) precisely so the export can throw it away again.
 *
 * What replaces it: the two crowns facing each other across an embrasure each
 * expose an ARC where their wall used to sit — the very chain of scan vertices
 * segmentation filled from. Loft those two arcs into a band and the space
 * between the posed crowns is closed by a surface whose every boundary vertex
 * is shared with a crown, so the export's seam fusion welds it instead of
 * splitting T-junctions. No fitting, no voxels, no search: the correspondence
 * is a ruled surface between two polylines.
 *
 * The arcs are not searched for either. A face is either scan (index < the
 * handover index) or generated (index ≥ it), and the arc is exactly the set of
 * edges with a scan face on one side and a generated face on the other — the
 * seam where the wall was welded on. Deleting the wall turns those edges into
 * the mesh border, so the arc is where the crown is open, by construction.
 *
 * Winding follows the same rule the skirt uses: for a border edge the kept
 * surface walks a→b, whatever attaches to it must contain b→a. So the arcs are
 * stored REVERSED relative to their scan face, and a band that walks both arcs
 * forward continues both crowns' orientation — which also means the two arcs
 * are traversed antiparallel in space, and the pairing has to cross (A's start
 * meets B's end). `arcsAlignedAfterReverse` checks that and warns when the
 * geometry says otherwise, rather than silently emitting a twisted band.
 */

export type InterproximalRebuildOptions = {
  /**
   * How close two arcs must COME to each other (mm) to be taken as the two
   * sides of one embrasure.
   *
   * Closest approach, not mean distance. Two facing arcs touch near the
   * contact and near the papilla anchors, but between those they bound the
   * black triangle and stand millimetres apart — measured on a real upper
   * jaw, genuine pairs average 3–4 mm, which is the same range as arcs a
   * whole tooth away. Their closest approach separates them cleanly: facing
   * arcs meet, everything else is a tooth width off.
   *
   * At an extraction space nothing meets, so those neighbours are capped
   * rather than lofted — which is right, there is real gum between them.
   */
  maxContactGapMm?: number;
  /** Arcs shorter than this many vertices are ignored (noise, not a wall). */
  minArcVertices?: number;
};

export type InterproximalRebuildStats = {
  /** Faces deleted per source mesh, in the order the sources were passed. */
  stripped: number[];
  /** Arcs found on all sources together. */
  arcs: number;
  /**
   * Arcs found per source. A crown in the middle of the arch should show 2 —
   * one per neighbour. Anything else means segmentation built no wall there,
   * or the seam came out fragmented.
   */
  arcsPerSource: number[];
  /** Arc pairs lofted into a band. */
  bridged: number;
  /** Arcs with no partner, closed with a fan instead. */
  capped: number;
  /** Triangles the rebuild produced. */
  triangles: number;
  /** Mean gap (mm) of the bridged pairs — how far the crowns stand apart. */
  meanGapMm: number;
  /**
   * One entry per band: the two arcs' vertex counts and how far apart they
   * stand. A healthy pair is two arcs of comparable length a fraction of a
   * millimetre apart; wildly different counts mean one side's arc was
   * truncated and the "band" is really a fan off the short one.
   */
  bands: Array<{ a: number; b: number; gapMm: number; contactMm: number }>;
  /** Vertex count of every arc that found no partner and was capped instead. */
  caps: number[];
  /**
   * The nearest partner each capped arc DID have, and how close it came. This
   * is what says whether the pairing threshold is wrong or whether the arcs
   * themselves came out broken.
   */
  nearestRejected: Array<{ verts: number; contactMm: number }>;
  /** Bands whose arcs came out running the same way — see `orientArcPair`. */
  reorientedBands: number;
};

type Arc = {
  /** Index of the source mesh, so two arcs of the SAME crown never pair up. */
  source: number;
  points: Vector3[];
  centroid: Vector3;
};

const DEFAULTS = {
  maxContactGapMm: 2.5,
  minArcVertices: 3,
};

/**
 * Drops the generated face suffix from `geometry`, in place.
 *
 * `from` is the handover index: faces [from, end) are generated, everything
 * before is scan. Null/undefined (nothing generated, or segmentation could not
 * identify it) leaves the geometry untouched.
 *
 * @returns how many faces were removed
 */
export function stripGeneratedFaces(
  geometry: BufferGeometry,
  from: number | null | undefined,
): number {
  const index = geometry.getIndex();
  if (!index || from === null || from === undefined) return 0;
  const faceCount = index.count / 3;
  if (from < 0 || from >= faceCount) return 0;
  if (from === 0) {
    console.warn(
      'stripGeneratedFaces: the whole mesh is marked generated — refusing to empty it',
    );
    return 0;
  }
  const kept = new Uint32Array(from * 3);
  for (let i = 0; i < kept.length; i++) kept[i] = index.getX(i);
  geometry.setIndex(new BufferAttribute(kept, 1));
  return faceCount - from;
}

/**
 * The arcs where the generated geometry was welded onto the scan: edges with
 * exactly one scan face and at least one generated face, chained into
 * polylines and directed so they read as the stripped mesh's border.
 *
 * Call BEFORE stripping — afterwards the generated side is gone and the seam
 * is indistinguishable from the rest of the border.
 */
export function extractGeneratedSeamArcs(
  geometry: BufferGeometry,
  from: number | null | undefined,
  minArcVertices = DEFAULTS.minArcVertices,
): Vector3[][] {
  const index = geometry.getIndex();
  const position = geometry.getAttribute('position');
  if (!index || !position || from === null || from === undefined) return [];
  const faceCount = index.count / 3;
  if (from <= 0 || from >= faceCount) return [];

  // For each undirected edge: how many scan / generated faces use it, and in
  // which direction the scan face walks it.
  const scanUse = new Map<number, number>();
  const generatedUse = new Map<number, number>();
  const scanDirection = new Map<number, [number, number]>();
  const vertexCount = position.count;
  const key = (a: number, b: number) => (a < b ? a * vertexCount + b : b * vertexCount + a);

  for (let f = 0; f < faceCount; f++) {
    const generated = f >= from;
    const a = index.getX(f * 3);
    const b = index.getX(f * 3 + 1);
    const c = index.getX(f * 3 + 2);
    const corners: Array<[number, number]> = [
      [a, b],
      [b, c],
      [c, a],
    ];
    for (const [u, v] of corners) {
      const k = key(u, v);
      if (generated) {
        generatedUse.set(k, (generatedUse.get(k) ?? 0) + 1);
      } else {
        scanUse.set(k, (scanUse.get(k) ?? 0) + 1);
        scanDirection.set(k, [u, v]);
      }
    }
  }

  // Reversed relative to the scan face — see the winding note in the header.
  const seam: Array<[number, number]> = [];
  for (const [k, uses] of scanUse) {
    if (uses !== 1) continue;
    if ((generatedUse.get(k) ?? 0) === 0) continue;
    const [u, v] = scanDirection.get(k)!;
    seam.push([v, u]);
  }

  const chains = chainDirectedEdges(seam);
  // A wall's seam is an OPEN border arc by construction. Closed chains mean
  // the "generated block" is not the wall — the classic cause is a marker
  // applied to a geometry whose face order was reordered after the write
  // (computeBoundsTree sorts the index in place), which turns the suffix
  // into a random spatial cluster and its boundary into closed loops.
  const closed = chains.filter((chain) => chain.length > 1 && chain[0] === chain[chain.length - 1]);
  if (closed.length > 0) {
    console.warn(
      `extractGeneratedSeamArcs: ${closed.length}/${chains.length} seam chain(s) are CLOSED ` +
        `loops — the generated-face marker does not line up with this geometry's face order ` +
        `(stripping it would cut holes, not remove the wall)`,
    );
  }
  const point = new Vector3();
  return chains
    .filter((chain) => chain.length >= minArcVertices)
    .map((chain) =>
      chain.map((vertex) => new Vector3().copy(point.fromBufferAttribute(position, vertex))),
    );
}

/**
 * Strips every source's generated faces and returns the surface that replaces
 * them: a band per facing arc pair, a fan per arc with no partner.
 *
 * The sources are mutated (their index is truncated); the returned geometry is
 * new and standalone — the export merges it in with everything else, and the
 * seam fusion welds its borders onto the crowns because the positions are
 * literally the crowns' own vertices.
 *
 * @param sources world-baked crown geometries
 * @param generatedFrom the handover index per source, aligned with `sources`
 */
export function rebuildInterproximalSpace(
  sources: BufferGeometry[],
  generatedFrom: Array<number | null | undefined>,
  options: InterproximalRebuildOptions = {},
): { geometry: BufferGeometry | null; stats: InterproximalRebuildStats } {
  const maxContactGapMm = options.maxContactGapMm ?? DEFAULTS.maxContactGapMm;
  const minArcVertices = options.minArcVertices ?? DEFAULTS.minArcVertices;

  const arcs: Arc[] = [];
  const stripped: number[] = [];
  const arcsPerSource: number[] = [];
  sources.forEach((geometry, source) => {
    const found = extractGeneratedSeamArcs(geometry, generatedFrom[source], minArcVertices);
    for (const points of found) {
      arcs.push({ source, points, centroid: centroidOf(points) });
    }
    arcsPerSource.push(found.length);
    stripped.push(stripGeneratedFaces(geometry, generatedFrom[source]));
  });

  const positions: number[] = [];
  const indices: number[] = [];
  const stats: InterproximalRebuildStats = {
    stripped,
    arcs: arcs.length,
    arcsPerSource,
    bridged: 0,
    capped: 0,
    triangles: 0,
    meanGapMm: 0,
    bands: [],
    caps: [],
    nearestRejected: [],
    reorientedBands: 0,
  };

  let gapTotal = 0;
  const { partner, nearest } = matchFacingArcs(arcs, maxContactGapMm);
  const handled = new Set<number>();
  for (let i = 0; i < arcs.length; i++) {
    const j = partner[i];
    if (j < 0 || handled.has(i)) continue;
    handled.add(i);
    handled.add(j);
    const proximity = arcProximity(arcs[i].points, arcs[j].points);
    gapTotal += proximity.mean;
    stats.bridged++;
    stats.bands.push({
      a: arcs[i].points.length,
      b: arcs[j].points.length,
      gapMm: proximity.mean,
      contactMm: proximity.closest,
    });
    if (loftArcs(arcs[i].points, arcs[j].points, positions, indices)) stats.reorientedBands++;
  }
  for (let i = 0; i < arcs.length; i++) {
    if (handled.has(i)) continue;
    stats.capped++;
    stats.caps.push(arcs[i].points.length);
    stats.nearestRejected.push({ verts: arcs[i].points.length, contactMm: nearest[i] });
    capArc(arcs[i].points, positions, indices);
  }

  stats.triangles = indices.length / 3;
  stats.meanGapMm = stats.bridged > 0 ? gapTotal / stats.bridged : 0;
  if (stats.triangles === 0) return { geometry: null, stats };

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  return { geometry, stats };
}

/**
 * A band between two arcs that face each other across an embrasure.
 *
 * The arcs are walked FORWARD together, advancing whichever is behind in
 * normalized arc length, so the strip follows both regardless of how
 * differently the two crowns were tessellated. `b` is reversed first: the two
 * arcs run antiparallel in space (each is directed as its own crown's border),
 * so pairing them head-to-head would twist the band into an hourglass.
 *
 * @returns whether the pair had to be re-oriented against the border rule
 */
function loftArcs(a: Vector3[], b: Vector3[], positions: number[], indices: number[]): boolean {
  const left = a;
  const { right, reoriented } = orientArcPair(left, b);
  if (left.length < 2 || right.length < 2) return false;

  const base = positions.length / 3;
  for (const p of left) positions.push(p.x, p.y, p.z);
  const rightBase = positions.length / 3;
  for (const p of right) positions.push(p.x, p.y, p.z);

  const tLeft = normalizedArcLength(left);
  const tRight = normalizedArcLength(right);
  let k = 0;
  let l = 0;
  while (k < left.length - 1 || l < right.length - 1) {
    const advanceLeft =
      l >= right.length - 1 || (k < left.length - 1 && tLeft[k + 1] <= tRight[l + 1]);
    if (advanceLeft) {
      // Contains left[k] → left[k+1]: continues crown A's border.
      indices.push(base + k, base + k + 1, rightBase + l);
      k++;
    } else {
      // Contains right[l+1] → right[l]: continues crown B's border.
      indices.push(base + k, rightBase + l + 1, rightBase + l);
      l++;
    }
  }
  return reoriented;
}

/**
 * Which way round to walk `b` against `a`.
 *
 * Facing arcs are antiparallel — each is directed as its own crown's border,
 * and the two crowns face each other — so the default is `b` reversed. But
 * that has to be CHECKED, not assumed: a scan with a locally flipped crown, or
 * an arc that only covers part of the embrasure, produces a pair that reads
 * the other way, and lofting it as if it were antiparallel twists the band
 * into an hourglass — long triangles crossing at a pinch point, which is what
 * a fan looks like in a viewport.
 *
 * Comparing the two ENDPOINTS is not enough when the arcs cover different
 * spans (79 vertices against 99 on real crowns). This walks both candidate
 * orientations point by point at matched arc length and takes the cheaper —
 * the whole correspondence, not its two ends.
 *
 * When the reversed reading loses, the band cannot satisfy both crowns'
 * border rule at once (that is only possible for an antiparallel pair), so it
 * follows `a` and leaves `b`'s side to the manifold repair's winding
 * harmonisation, which is already running downstream.
 */
function orientArcPair(a: Vector3[], b: Vector3[]): { right: Vector3[]; reoriented: boolean } {
  const reversed = [...b].reverse();
  const costReversed = correspondenceCost(a, reversed);
  const costForward = correspondenceCost(a, b);
  return costReversed <= costForward
    ? { right: reversed, reoriented: false }
    : { right: b, reoriented: true };
}

/** Mean distance between the two polylines at matched normalized arc length. */
function correspondenceCost(a: Vector3[], b: Vector3[]): number {
  const tA = normalizedArcLength(a);
  const tB = normalizedArcLength(b);
  let cost = 0;
  let j = 0;
  for (let i = 0; i < a.length; i++) {
    while (j < b.length - 1 && tB[j + 1] < tA[i]) j++;
    cost += a[i].distanceTo(b[j]);
  }
  return cost / a.length;
}

/**
 * Closes an arc with no partner — the mesial side of the first molar, the
 * distal side of the last one — with a fan to its centroid, which is what the
 * segmentation wall was at those places anyway. The arc is treated as closed
 * (last vertex back to first), the same wrap-around the ring fill used.
 */
function capArc(arc: Vector3[], positions: number[], indices: number[]): void {
  if (arc.length < 3) return;
  const base = positions.length / 3;
  for (const p of arc) positions.push(p.x, p.y, p.z);
  const centre = centroidOf(arc);
  const centreIndex = positions.length / 3;
  positions.push(centre.x, centre.y, centre.z);
  for (let k = 0; k < arc.length; k++) {
    const next = (k + 1) % arc.length;
    indices.push(base + k, base + next, centreIndex);
  }
}

/**
 * Mutual nearest neighbours across DIFFERENT sources, within
 * `maxPairDistanceMm`. Mutual, not greedy: a molar's distal arc must be the
 * best match for the next tooth's mesial arc as well, so one crooked tooth
 * cannot capture an arc that belongs to its neighbour.
 *
 * @returns `partner[i]` = the index of arc i's partner or −1, and `nearest[i]`
 *          = the distance to its closest candidate whatever the outcome, so a
 *          rejection can be told apart from "there was nothing there"
 */
function matchFacingArcs(
  arcs: Arc[],
  maxContactGapMm: number,
): { partner: number[]; nearest: number[] } {
  const best = new Array<number>(arcs.length).fill(-1);
  const bestDistance = new Array<number>(arcs.length).fill(Infinity);
  const nearest = new Array<number>(arcs.length).fill(Infinity);
  for (let i = 0; i < arcs.length; i++) {
    for (let j = i + 1; j < arcs.length; j++) {
      if (arcs[i].source === arcs[j].source) continue;
      // Centroids first — the full point-to-point pass is only worth running
      // for candidates that are anywhere near each other.
      if (arcs[i].centroid.distanceTo(arcs[j].centroid) > 20) continue;
      const { closest } = arcProximity(arcs[i].points, arcs[j].points);
      nearest[i] = Math.min(nearest[i], closest);
      nearest[j] = Math.min(nearest[j], closest);
      if (closest > maxContactGapMm) continue;
      if (closest < bestDistance[i]) {
        bestDistance[i] = closest;
        best[i] = j;
      }
      if (closest < bestDistance[j]) {
        bestDistance[j] = closest;
        best[j] = i;
      }
    }
  }
  return {
    partner: best.map((j, i) => (j >= 0 && best[j] === i ? j : -1)),
    nearest,
  };
}

/**
 * How two polylines sit relative to each other.
 *
 * `closest` is what decides whether they bound the same embrasure: the 5th
 * percentile of the per-point nearest distances, i.e. where the two arcs come
 * together, robust to a single stray vertex. `mean` is reported only — for
 * facing arcs it measures the black triangle they bound, which says nothing
 * about whether they belong together.
 */
function arcProximity(a: Vector3[], b: Vector3[]): { closest: number; mean: number } {
  const nearest = [...nearestDistances(a, b), ...nearestDistances(b, a)];
  const mean = nearest.reduce((sum, d) => sum + d, 0) / nearest.length;
  nearest.sort((x, y) => x - y);
  const percentile = Math.min(nearest.length - 1, Math.floor(nearest.length * 0.05));
  return { closest: nearest[percentile], mean };
}

function nearestDistances(from: Vector3[], to: Vector3[]): number[] {
  return from.map((p) => {
    let nearest = Infinity;
    for (const q of to) {
      const d = p.distanceToSquared(q);
      if (d < nearest) nearest = d;
    }
    return Math.sqrt(nearest);
  });
}

function normalizedArcLength(points: Vector3[]): number[] {
  const t = new Array<number>(points.length).fill(0);
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += points[i].distanceTo(points[i - 1]);
    t[i] = total;
  }
  if (total <= 0) return t.map((_, i) => i / Math.max(1, points.length - 1));
  return t.map((v) => v / total);
}

function centroidOf(points: Vector3[]): Vector3 {
  const centre = new Vector3();
  for (const p of points) centre.add(p);
  return centre.divideScalar(Math.max(1, points.length));
}

/**
 * Chains directed edges into polylines. Open chains are emitted from their
 * start; whatever is left over is a cycle and gets walked from an arbitrary
 * vertex. A vertex may carry several outgoing seam edges — both of a crown's
 * walls can end on the same papilla anchor — so the edges are consumed one by
 * one instead of being kept in a vertex→vertex map.
 */
function chainDirectedEdges(edges: Array<[number, number]>): number[][] {
  const outgoing = new Map<number, number[]>();
  const incoming = new Map<number, number>();
  edges.forEach(([u, v], edge) => {
    const list = outgoing.get(u);
    if (list) list.push(edge);
    else outgoing.set(u, [edge]);
    incoming.set(v, (incoming.get(v) ?? 0) + 1);
  });

  const used = new Uint8Array(edges.length);
  const chains: number[][] = [];

  const walk = (start: number): number[] => {
    const chain = [start];
    let current = start;
    for (;;) {
      const list = outgoing.get(current);
      if (!list) break;
      let next = -1;
      while (list.length > 0) {
        const edge = list.pop()!;
        if (!used[edge]) {
          next = edge;
          break;
        }
      }
      if (next < 0) break;
      used[next] = 1;
      current = edges[next][1];
      chain.push(current);
      if (current === start) break;
    }
    return chain;
  };

  for (const start of outgoing.keys()) {
    if ((incoming.get(start) ?? 0) > 0) continue;
    const chain = walk(start);
    if (chain.length > 1) chains.push(chain);
  }
  for (let edge = 0; edge < edges.length; edge++) {
    if (used[edge]) continue;
    const chain = walk(edges[edge][0]);
    if (chain.length > 1) chains.push(chain);
  }
  return chains;
}
